/**
 * Data mappers for converting GraphQL User types to legacy interfaces
 *
 * These mappers ensure compatibility between the new GraphQL data structures
 * and existing component interfaces, allowing gradual migration.
 */

import type {
  IUserDetail,
  IUserOrganizationDetails,
  ISystemOrganization,
} from '../../../../ts/interface/userDetail.interface';
import { UserDetailStatus } from '../../../../ts/interface/userDetail.interface';
import { convertCentsToMoney } from '../../../../utils';
import type { OrganizationSystemGraphQL } from './useUserByPhone';
import type { UserGraphQL, UserSystemGraphQL } from './useUsersRelay';
import type {
  UserDetailGraphQL,
  UserSystemDetailGraphQL,
  RfidCardGraphQL,
  UserMembershipGraphQL,
  UserWalletGraphQL,
  UserRentalGraphQL,
  ErrorReportGraphQL,
  MembershipGraphQL,
  WalletTransactionGraphQL,
  ChargeGraphQL,
} from './useUserDetailRelay';
import type { IRental } from '../../../../ts/interface/rental.interface';
import type { IProblem } from '../../../../ts/interface/problem.interface';
import type { IUserMembership, IMembership } from '../../../../ts/interface/membership.interface';
import type { IRfidCard } from '../../../../ts/interface/rfidCard.interface.tsx';

// ============================================================================
// Status Derivation
// ============================================================================

/**
 * Derive user status from UserSystem fields
 * Maps GraphQL userSystem state to legacy status strings
 */
export function deriveUserStatus(
  userSystem?: UserSystemGraphQL | UserSystemDetailGraphQL | null
): UserDetailStatus {
  if (!userSystem) return UserDetailStatus.ACTIVE;
  if (userSystem.blacklisted) return UserDetailStatus.BLACKLISTED;
  if (userSystem.removed) return UserDetailStatus.INACTIVE;
  if (userSystem.rejected) return UserDetailStatus.INACTIVE;
  return UserDetailStatus.ACTIVE;
}

/**
 * Get the user system for a specific system from the array.
 *
 * When `organizationId` is provided, matches on that first (for org context);
 * otherwise matches on `systemId`. Falls back to the first entry.
 */
export function findUserSystemForSystem(
  userSystems: (UserSystemGraphQL | UserSystemDetailGraphQL)[] | undefined,
  systemId?: string,
  organizationId?: string
): UserSystemGraphQL | UserSystemDetailGraphQL | undefined {
  if (!userSystems) return undefined;
  if (organizationId) {
    return (
      userSystems.find((us) => us.organizationId === organizationId) ??
      userSystems[0]
    );
  }
  if (!systemId) return userSystems[0];
  return userSystems.find((us) => us.systemId === systemId) ?? userSystems[0];
}

// ============================================================================
// User List Mappers
// ============================================================================

/**
 * Map a GraphQL User to the legacy IUserDetail interface for list views
 *
 * @param user - The GraphQL user data
 * @param systemId - Optional system ID to get per-system status
 * @returns IUserDetail compatible object
 */
export function mapRelayUserToIUserDetail(
  user: UserGraphQL,
  systemId?: string
): IUserDetail {
  // Get system-specific status
  const userSystem = findUserSystemForSystem(user.userSystems, systemId);

  return {
    id: user.id,
    phone: user.phone,
    fullName: user.fullName ?? `${user.firstName} ${user.lastName}`,
    emailAddress: user.email ?? '',
    joined: userSystem?.joined ?? user.createdOn ?? '',
    banned: userSystem?.blacklisted ?? false,
    hidden: userSystem?.hidden ?? false,
    removed: userSystem?.removed ?? false,
    has_membership: (user.userMemberships?.length ?? 0) > 0,
    status: deriveUserStatus(userSystem),
    acceptedTOSOn: userSystem?.acceptedTosOn ?? '',
    // Fields not available in list query - provide defaults
    amount_due: 0,
    appVersion: '',
    autoGenNumber: false,
    deviceCarrier: '',
    deviceModel: '',
    deviceOS: '',
    deviceOSVersion: '',
  };
}

/**
 * Map an array of GraphQL users to IUserDetail array
 */
export function mapRelayUsersToIUserDetails(
  users: UserGraphQL[],
  systemId?: string
): IUserDetail[] {
  return users.map((user) => mapRelayUserToIUserDetail(user, systemId));
}

// ============================================================================
// User Detail Mappers
// ============================================================================

/**
 * Map a GraphQL UserDetail to the legacy IUserDetail interface
 *
 * @param user - The GraphQL user detail data
 * @param systemId - Optional system ID to get per-system status
 * @param organizationId - Optional organization ID (takes precedence over systemId for per-system lookup)
 * @returns IUserDetail compatible object with additional detail fields
 */
/**
 * Selects the wallet that belongs to the given partner. A user can have wallets
 * across multiple partners, so wallet info on the user details page must be
 * scoped to the partner the current system belongs to. Falls back to the first
 * wallet when no partner is provided (e.g. org context, where wallets aren't shown).
 */
function findWalletForPartner(
  wallets: UserDetailGraphQL['userWallets'],
  systemPartnerOldId?: number | null
) {
  if (!wallets || wallets.length === 0) return undefined;
  if (systemPartnerOldId == null) return wallets[0];
  return wallets.find((w) => w.partnerOldId === systemPartnerOldId);
}

export function mapRelayUserDetailToIUserDetail(
  user: UserDetailGraphQL,
  systemId?: string,
  organizationId?: string,
  systemPartnerOldId?: number | null
): IUserDetail {
  // Get system-specific (or org-specific) status
  const userSystem = findUserSystemForSystem(
    user.userSystems,
    systemId,
    organizationId
  );

  // Get device info
  const device = user.latestDevice;

  // Scope wallet info to the partner the current system belongs to
  const partnerWallet = findWalletForPartner(user.userWallets, systemPartnerOldId);

  return {
    id: user.id,
    phone: user.phone,
    fullName: user.fullName ?? `${user.firstName} ${user.lastName}`,
    emailAddress: user.email ?? '',
    joined: userSystem?.joined ?? user.createdOn ?? '',
    banned: userSystem?.blacklisted ?? false,
    hidden: userSystem?.hidden ?? false,
    removed: userSystem?.removed ?? false,
    has_membership: (user.userMemberships?.length ?? 0) > 0,
    status: deriveUserStatus(userSystem),
    acceptedTOSOn: userSystem?.acceptedTosOn ?? '',
    // Detail fields from GraphQL
    amount_due: userSystem?.amountDue ?? 0,
    appVersion: device?.appVersion ?? '',
    autoGenNumber: user.autoGenNumber ?? false,
    deviceCarrier: device?.carrier ?? '',
    deviceModel: device?.model ?? '',
    deviceOS: device?.os ?? '',
    deviceOSVersion: device?.osVersion ?? '',
    walletBalance: partnerWallet?.balance ?? 0,
    hasWallet: Boolean(partnerWallet),
  };
}

/**
 * Build the ISystemOrganization[] array for org context.
 *
 * Cross-references `user.userSystems` with the `organizationSystems` data fetched
 * by useUserByPhoneQuery in org context, filtering to the systems within the org
 * that the user has joined. Produces the shape IUserOrganizationDetails.systems expects.
 *
 * When a system can't be resolved (e.g. org has more systems than the 100-item
 * query limit), a fallback entry is created using the GID so the page still renders.
 */
export function buildUserOrganizationSystems(
  user: UserDetailGraphQL,
  organizationSystems: OrganizationSystemGraphQL[],
  organizationId: string
): ISystemOrganization[] {
  if (!organizationId) return [];
  const orgSystemMap = new Map(
    organizationSystems.map((sys) => [sys.id, sys])
  );
  return (user.userSystems ?? [])
    .filter((us) => us.organizationId === organizationId && us.systemId)
    .map((us) => {
      const sys = orgSystemMap.get(us.systemId as string);
      return {
        id: sys?.oldId ?? us.systemId ?? '',
        old_id: sys?.oldId ?? '',
        name: sys?.name ?? us.systemId ?? '',
      };
    });
}

/**
 * Map a GraphQL UserDetail to the organization-details legacy interface.
 * Requires `organizationSystems` to resolve system names / oldIds.
 */
export function mapRelayUserDetailToIUserOrganizationDetails(
  user: UserDetailGraphQL,
  organizationId: string,
  organizationSystems: OrganizationSystemGraphQL[]
): IUserOrganizationDetails {
  const base = mapRelayUserDetailToIUserDetail(user, undefined, organizationId);
  return {
    ...base,
    organization: organizationId,
    role: '',
    systems: buildUserOrganizationSystems(user, organizationSystems, organizationId),
  };
}

// ============================================================================
// Extended User Detail Interface
// ============================================================================

/**
 * Extended user detail with all GraphQL relationships
 */
export interface IUserDetailExtended extends IUserDetail {
  firstName: string;
  lastName: string;
  language: string | null;
  region: string;
  delinquent: boolean;
  createdOn: string | null;
  deletedOn: string | null;
  authStatus: number | null;
  organizationId: string | null;
  // Related data
  rfidCards: RfidCardGraphQL[];
  memberships: UserMembershipGraphQL[];
  wallets: UserWalletGraphQL[];
  rentals: UserRentalGraphQL[];
  userSystems: UserSystemDetailGraphQL[];
  // Computed
  walletBalance: number;
  hasMembership: boolean;
}

/**
 * Map GraphQL UserDetail to extended interface with all relationships
 */
export function mapRelayUserDetailToExtended(
  user: UserDetailGraphQL,
  systemId?: string
): IUserDetailExtended {
  const userSystem = findUserSystemForSystem(user.userSystems, systemId);
  const walletBalance = user.userWallets?.[0]?.balance ?? 0;
  const device = user.latestDevice;

  return {
    // Base IUserDetail fields
    id: user.id,
    phone: user.phone,
    fullName: user.fullName ?? `${user.firstName} ${user.lastName}`,
    emailAddress: user.email ?? '',
    joined: userSystem?.joined ?? user.createdOn ?? '',
    banned: userSystem?.blacklisted ?? false,
    hidden: userSystem?.hidden ?? false,
    removed: userSystem?.removed ?? false,
    has_membership: (user.userMemberships?.length ?? 0) > 0,
    status: deriveUserStatus(userSystem),
    acceptedTOSOn: userSystem?.acceptedTosOn ?? '',
    amount_due: userSystem?.amountDue ?? 0,
    appVersion: device?.appVersion ?? '',
    autoGenNumber: user.autoGenNumber ?? false,
    deviceCarrier: device?.carrier ?? '',
    deviceModel: device?.model ?? '',
    deviceOS: device?.os ?? '',
    deviceOSVersion: device?.osVersion ?? '',

    // Extended fields
    firstName: user.firstName,
    lastName: user.lastName,
    language: user.language ?? null,
    region: user.region,
    delinquent: user.delinquent ?? false,
    createdOn: user.createdOn ?? null,
    deletedOn: user.deletedOn ?? null,
    authStatus: user.authStatus ?? null,
    organizationId: user.organizationIds?.[0] ?? null,

    // Related data
    rfidCards: user.rfidCards ?? [],
    memberships: user.userMemberships ?? [],
    wallets: user.userWallets ?? [],
    rentals: user.rentals ?? [],
    userSystems: user.userSystems ?? [],

    // Computed
    walletBalance,
    hasMembership: (user.userMemberships?.length ?? 0) > 0,
  };
}

// ============================================================================
// RFID Card Mappers
// ============================================================================

/**
 * Legacy RFID card interface
 */
export interface ILegacyRfidCard {
  id: string;
  cardId: string;
  label: string;
  isActive: boolean;
  archivedOn: string | null;
  createdOn: string;
}

/**
 * Map GraphQL RFID card to legacy interface
 */
export function mapRelayRfidCardToLegacy(card: RfidCardGraphQL): ILegacyRfidCard {
  return {
    id: card.id,
    cardId: card.cardId ?? '',
    label: card.label ?? '',
    isActive: card.isActive ?? false,
    archivedOn: card.archivedOn ?? null,
    createdOn: card.createdOn ?? '',
  };
}

/**
 * Map array of GraphQL RFID cards to legacy format
 */
export function mapRelayRfidCardsToLegacy(cards: RfidCardGraphQL[]): ILegacyRfidCard[] {
  return cards.map(mapRelayRfidCardToLegacy);
}

/**
 * Map GraphQL RFID card to IRfidCard interface (snake_case)
 */
export function mapRelayRfidCardToIRfidCard(card: RfidCardGraphQL): IRfidCard {
  return {
    id: card.id,
    card_id: card.cardId ?? '',
    label: card.label ?? '',
    archived_on: card.archivedOn ?? undefined,
    created_on: card.createdOn ?? '',
  };
}

/**
 * Map array of GraphQL RFID cards to IRfidCard array
 */
export function mapRelayRfidCardsToIRfidCards(cards: RfidCardGraphQL[]): IRfidCard[] {
  return cards.map(mapRelayRfidCardToIRfidCard);
}

// ============================================================================
// Membership Mappers
// ============================================================================

/**
 * Legacy membership interface
 */
export interface ILegacyMembership {
  id: string;
  approvedOn: string | null;
  canceledOn: string | null;
  expiresOn: string | null;
  rejectedOn: string | null;
  priority: number;
  membershipId: string;
  isActive: boolean;
}

/**
 * Map GraphQL membership to legacy interface
 */
export function mapRelayMembershipToLegacy(
  membership: UserMembershipGraphQL
): ILegacyMembership {
  const now = new Date();
  const expiresOn = membership.expiresOn ? new Date(membership.expiresOn) : null;
  const isActive =
    !!membership.approvedOn &&
    !membership.canceledOn &&
    !membership.rejectedOn &&
    (!expiresOn || expiresOn > now);

  return {
    id: membership.id,
    approvedOn: membership.approvedOn ?? null,
    canceledOn: membership.canceledOn ?? null,
    expiresOn: membership.expiresOn ?? null,
    rejectedOn: membership.rejectedOn ?? null,
    priority: membership.priority ?? 0,
    membershipId: membership.membershipOldId ?? '',
    isActive,
  };
}

/**
 * Map array of GraphQL memberships to legacy format
 */
export function mapRelayMembershipsToLegacy(
  memberships: UserMembershipGraphQL[]
): ILegacyMembership[] {
  return memberships.map(mapRelayMembershipToLegacy);
}

// ============================================================================
// Wallet Mappers
// ============================================================================

/**
 * Legacy wallet interface
 */
export interface ILegacyWallet {
  id: string;
  balance: number;
  currency: string;
  paymentProvider: string;
}

/**
 * Map GraphQL wallet to legacy interface
 */
export function mapRelayWalletToLegacy(wallet: UserWalletGraphQL): ILegacyWallet {
  return {
    id: wallet.id,
    balance: wallet.balance ?? 0,
    currency: wallet.currency,
    paymentProvider: wallet.paymentProvider ?? '',
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format wallet balance from cents to dollars
 */
export function formatWalletBalance(balanceCents: number, currency = 'USD'): string {
  const dollars = balanceCents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(dollars);
}

/**
 * Check if user has any delinquent status. When a system partner is provided,
 * the wallet balance is scoped to that partner's wallet (see findWalletForPartner).
 */
export function isUserDelinquent(
  user: UserDetailGraphQL | null,
  systemPartnerOldId?: number | null
): boolean {
  if (!user) return false;
  if (user.delinquent) return true;
  const walletBalance =
    findWalletForPartner(user.userWallets, systemPartnerOldId)?.balance ?? 0;
  return walletBalance < 0;
}

/**
 * Get display status for user (for UI badges)
 */
export function getUserDisplayStatus(
  user: UserDetailGraphQL | UserGraphQL,
  systemId?: string
): { label: string; color: 'success' | 'error' | 'warning' | 'info' } {
  const userSystem = findUserSystemForSystem(user.userSystems, systemId);

  if (userSystem?.blacklisted) {
    return { label: 'Banned', color: 'error' };
  }
  if (userSystem?.removed) {
    return { label: 'Removed', color: 'warning' };
  }
  if (userSystem?.rejected) {
    return { label: 'Rejected', color: 'error' };
  }
  if (userSystem?.hidden) {
    return { label: 'Hidden', color: 'info' };
  }
  return { label: 'Active', color: 'success' };
}

// ============================================================================
// Error Report / Problem Mappers
// ============================================================================

/**
 * Map GraphQL error report to legacy IProblem interface
 */
export function mapRelayErrorReportToProblem(
  errorReport: ErrorReportGraphQL,
  userName?: string
): IProblem {
  return {
    id: errorReport.id,
    state: errorReport.state ?? 0,
    content: errorReport.content ?? '',
    created: errorReport.created ?? '',
    userName: userName ?? '',
    tags: errorReport.tags ?? [],
  };
}

/**
 * Map array of GraphQL error reports to IProblem array
 */
export function mapRelayErrorReportsToProblem(
  errorReports: ErrorReportGraphQL[],
  userName?: string
): IProblem[] {
  return errorReports.map((report) => mapRelayErrorReportToProblem(report, userName));
}

// ============================================================================
// User Membership Mappers
// ============================================================================

/**
 * Map GraphQL Membership to legacy IMembership interface
 */
export function mapRelayMembershipToIMembership(
  membership: MembershipGraphQL
): Partial<IMembership> {
  return {
    id: membership.oldId ?? membership.id,
    name: membership.name,
    auth_type: membership.authType ?? '',
    archived_on: membership.archivedOn ?? null,
    // System info - only IDs available from GraphQL
    system: membership.systemOldId
      ? ({
          old_id: membership.systemOldId,
          id: membership.systemId ?? '',
        } as any)
      : undefined,
  };
}

/**
 * Map GraphQL UserMembership to legacy IUserMembership interface
 */
export function mapRelayUserMembershipToIUserMembership(
  userMembership: UserMembershipGraphQL
): IUserMembership {
  const membership = userMembership.membership
    ? mapRelayMembershipToIMembership(userMembership.membership)
    : {};

  return {
    id: userMembership.id,
    oldId: userMembership.oldId ?? null,
    membership: membership as IMembership,
    created_on: userMembership.createdOn ?? null,
    approved_on: userMembership.approvedOn ?? null,
    canceled_on: userMembership.canceledOn ?? null,
    rejected_on: userMembership.rejectedOn ?? null,
    expires_on: userMembership.expiresOn ?? null,
    approved: userMembership.isApproved ?? false,
    canceled: userMembership.isCanceled ?? false,
    expired: userMembership.isExpired ?? false,
    active: userMembership.isActive ?? false,
    rejected: userMembership.isRejected ?? false,
  };
}

/**
 * Map array of GraphQL UserMemberships to IUserMembership array
 */
export function mapRelayUserMembershipsToIUserMemberships(
  userMemberships: UserMembershipGraphQL[]
): IUserMembership[] {
  return userMemberships.map(mapRelayUserMembershipToIUserMembership);
}

// ============================================================================
// Rental Mappers
// ============================================================================

/**
 * Map GraphQL Rental to legacy IRental interface (partial)
 * Note: Some fields from REST API are not available in GraphQL
 */
export function mapRelayRentalToIRental(rental: UserRentalGraphQL): Partial<IRental> {
  return {
    id: rental.id, // Use GraphQL UUID for routing
    gid: rental.id,
    old_id: rental.oldId ?? '',
    startTime: rental.startTime ?? '',
    endTime: rental.endTime ?? null,
    duration: rental.duration ?? 0,
    chargeAmount: rental.chargeAmount ?? 0,
    // Hardware info
    hardware: rental.hardware
      ? {
          id: rental.hardware.oldId ?? rental.hardware.id,
          number: rental.hardware.number ?? 0,
          type_name: rental.hardware.hardwareType?.name ?? '',
        }
      : ({} as any),
    hardwareNumber: rental.hardware?.number ?? 0,
    // Station info
    start: {
      station: rental.startLocation
        ? {
            id: rental.startLocation.oldId ?? rental.startLocation.id,
            name: rental.startLocation.name,
            longitude: 0,
            latitude: 0,
          }
        : { id: '', name: '', longitude: 0, latitude: 0 },
      time: rental.startTime ?? '',
      triggered_by_type: '',
    },
    end: rental.endTime
      ? {
          station: rental.endLocation
            ? {
                id: rental.endLocation.oldId ?? rental.endLocation.id,
                name: rental.endLocation.name,
                longitude: 0,
                latitude: 0,
              }
            : { id: '', name: '', longitude: 0, latitude: 0 },
          time: rental.endTime,
          triggered_by_type: '',
        }
      : undefined,
    end_by: rental.endBy ?? undefined,
    // System info
    system: rental.system
      ? ({
          id: rental.system.oldId ?? rental.system.id,
          name: rental.system.name,
          old_id: rental.system.oldId ?? '',
        } as any)
      : undefined,
    // Billing - use chargeAmount as total
    billing: {
      total: rental.chargeAmount ?? 0,
      currency: 'USD',
      transactions: [],
      parking_credit: 0,
      usage_sub_total: 0,
      admin_modification: 0,
      admin_modification_reason: '',
      sub_total: rental.chargeAmount ?? 0,
      tax_rate: 0,
      tax_amount: 0,
      void_charge: false,
      void_by: { full_name: '', email: '' },
      voided_on: '',
      void_reason: '',
      pending_charge: { pending: false, note: '', decision_date: '', status: false },
      rate: {} as any,
      perk: {} as any,
      fees: [],
      coupon: {} as any,
    },
  };
}

/**
 * Map array of GraphQL Rentals to IRental array (partial)
 */
export function mapRelayRentalsToIRentals(rentals: UserRentalGraphQL[]): Partial<IRental>[] {
  return rentals.map(mapRelayRentalToIRental);
}

// ============================================================================
// Transaction Mappers
// ============================================================================

/**
 * Unified transaction interface for display
 */
export interface UnifiedTransaction {
  id: string;
  created: string;
  description: string;
  type: string;
  amount: string;
  status: string | null;
  source: 'wallet' | 'charge';
  userName?: string;
}

/**
 * Map GraphQL WalletTransaction to unified transaction format
 */
export function mapWalletTransactionToUnified(
  transaction: WalletTransactionGraphQL
): UnifiedTransaction {
  // Format amount - wallet transactions store in cents
  const amountCents = transaction.amount ?? 0;
  const amountFormatted = convertCentsToMoney(amountCents);

  return {
    // Use the gid `id` (not oldId) so row navigation resolves correctly on the
    // transaction detail page, which fetches via getWalletTransaction(id: gid).
    // Using oldId here caused the detail page to find nothing and fall back to
    // rendering an empty "Charge" page.
    id: transaction.id,
    created: transaction.createdOn ?? '',
    description: transaction.description ?? '',
    type: transaction.type ?? 'wallet',
    amount: amountFormatted,
    // Wallet transactions have no status field; the billing transactions list
    // (unified Transaction type) reports these as "succeeded", so match that
    // instead of falling back to a "Transfer" pill.
    status: 'succeeded',
    source: 'wallet',
  };
}

/**
 * Map GraphQL Charge to unified transaction format
 */
export function mapChargeToUnified(charge: ChargeGraphQL): UnifiedTransaction {
  // For holds (id starts with "pi_"), use holdAmount; otherwise use amount
  const isHold = charge.id.startsWith('pi_');
  const amountCents = (isHold ? charge.holdAmount : charge.amount) ?? 0;
  const amountFormatted = convertCentsToMoney(amountCents);

  return {
    id: charge.id,
    created: charge.created ?? '',
    description: charge.description ?? '',
    type: isHold ? 'hold' : 'charge',
    amount: amountFormatted,
    status: charge.refundStatus || charge.status || null,
    source: 'charge',
  };
}

/**
 * Combine and sort wallet transactions and charges into unified transaction list
 */
export function mapRelayTransactionsToUnified(
  walletTransactions: WalletTransactionGraphQL[] = [],
  charges: ChargeGraphQL[] = []
): UnifiedTransaction[] {
  const mappedWallet = walletTransactions.map(mapWalletTransactionToUnified);
  const mappedCharges = charges.map(mapChargeToUnified);

  // Combine and sort by created date (newest first)
  const combined = [...mappedWallet, ...mappedCharges];
  combined.sort((a, b) => {
    const dateA = new Date(a.created).getTime();
    const dateB = new Date(b.created).getTime();
    return dateB - dateA;
  });

  return combined;
}
